import { useContext, useEffect } from "react"

import { graphql, useStaticQuery } from "gatsby"
import styled from "styled-components"

import Button from "components/buttons/Button"
import { PopUpContext } from "components/Providers"
import FakeImage from "images/blog/defaultBlogImage.webp"
import colors from "styles/colors"
import media from "styles/media"
import text from "styles/text"
import UniversalLink from "utils/Loader/UniversalLink"

import Modal from "./Modal"

export default function PopUp() {
  const { openPopUp, canStart, setOpenPopUp, setCanStart } =
    useContext(PopUpContext)

  const popupData: Queries.PopupQuery = useStaticQuery(graphql`
    query Popup {
      allContentfulGlobalPopup(
        filter: { contentful_id: { ne: "28XMWHtGSBav6mEc3b8Ncq" } }
        sort: { startDate: ASC }
      ) {
        nodes {
          inlineImage {
            file {
              url
            }
            description
            height
            width
            ...ContentfulImageQuery
          }
          endDate
          id
          startDate
          startDelay
          text {
            text
          }
          title
          button {
            buttonText
            url
          }
          uniqueImage {
            file {
              url
            }
            description
            height
            width
            ...ContentfulImageQuery
          }
        }
      }
    }
  `)

  const activePopup = popupData.allContentfulGlobalPopup.nodes.find(popup => {
    const now = Date.now()
    const popupStart = new Date(popup.startDate || "Aug 31 1982").getTime()
    const popupEnd = new Date(popup.endDate || "Aug 31 2055").getTime()

    return popupStart <= now && popupEnd >= now
  })

  const handleButtonClick = () => {
    setCanStart(false)
    setOpenPopUp("")
  }

  const isExternal = activePopup?.button?.url?.includes("http")
  const routeStart = isExternal ? "" : "/"

  useEffect(() => {
    if (activePopup && activePopup.startDelay && canStart) {
      const time = activePopup.startDelay * 1000 ?? 3000
      setTimeout(() => {
        setOpenPopUp(activePopup.id)
      }, time)
    }
  }, [activePopup, setOpenPopUp, canStart])

  return (
    <>
      {activePopup && (
        <Modal
          open={!!openPopUp}
          setOpen={() => setOpenPopUp(openPopUp)}
          setShow={() => {}}
          disableScrollOnOpen={false}
        >
          {!activePopup.uniqueImage ? (
            <UniversalLink
              openInNewTab={isExternal}
              to={`${routeStart}${activePopup.button?.url ?? "/"}`}
            >
              {/* @ts-expect-error i think the following image will always be broken (this code needs refactoring) */}
              <BigImage src={activePopup.uniqueImage?.file?.url ?? ""} />
            </UniversalLink>
          ) : (
            <Wrapper>
              <Image src={FakeImage} alt="fake" />
              <Content>
                <Title>{activePopup.title}</Title>
                <Line />
                <Text>{activePopup.text?.text}</Text>
                <Button
                  styledLink
                  onClick={handleButtonClick}
                  href={`${routeStart}${activePopup.button?.url ?? "/"}`}
                  lightMode
                  arrow
                >
                  {activePopup.button?.buttonText}
                </Button>
              </Content>
            </Wrapper>
          )}
        </Modal>
      )}
    </>
  )
}

const BigImage = styled.img`
  max-height: 90vh;
  width: auto;
  max-width: 50vw;

  ${media.tablet} {
    width: auto;
    max-height: 60vh;
    max-width: 75vw;
  }
  ${media.mobile} {
    max-height: 60vh;
    width: auto;
    max-width: 89.333vw;
  }
`

const Wrapper = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 1000;
  max-height: 90%;
  background: ${colors.white};

  ${media.fullWidth} {
    width: 641px;
    border-radius: 20px;
    padding-bottom: 30px;
  }
  ${media.desktop} {
    width: 38.614vw;
    border-radius: 1.205vw;
    padding-bottom: 1.807vw;
  }
  ${media.tablet} {
    width: 62.598vw;
    border-radius: 1.953vw;
    padding-bottom: 2.93vw;
  }
  ${media.mobile} {
    width: 89.333vw;
    max-height: 70%;
    overflow: scroll;
    border-radius: 5.333vw;
    padding-bottom: 8vw;
  }
`

const Image = styled.img`
  width: 100%;
  height: auto;

  ${media.fullWidth} {
    border-radius: 20px;
    margin-bottom: 30px;
  }
  ${media.desktop} {
    border-radius: 1.205vw;
    margin-bottom: 1.807vw;
  }
  ${media.tablet} {
    border-radius: 1.953vw;
    margin-bottom: 2.93vw;
  }
  ${media.mobile} {
    border-radius: 5.333vw;
    margin-bottom: 8vw;
  }
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  ${media.fullWidth} {
    padding: 0 30px;
  }
  ${media.desktop} {
    padding: 0 1.807vw;
  }
  ${media.tablet} {
    padding: 0 2.93vw;
  }
  ${media.mobile} {
    padding: 0 5.333vw;
  }
`

const Title = styled.h5`
  ${text.h5};
  width: 100%;
  border-bottom: ${colors.primHorz} 1px solid;
  text-align: center;

  ${media.fullWidth} {
    margin-bottom: 30px;
  }
  ${media.desktop} {
    margin-bottom: 1.807vw;
  }
  ${media.tablet} {
    margin-bottom: 2.93vw;
  }
  ${media.mobile} {
    ${text.h6};
    margin-bottom: 8vw;
  }
`

const Line = styled.div`
  height: 1px;
  background: ${colors.magBlueHorz};
  width: 100%;
  ${media.fullWidth} {
    margin-bottom: 15px;
  }
  ${media.desktop} {
    margin-bottom: 0.904vw;
  }
  ${media.tablet} {
    margin-bottom: 1.465vw;
  }
  ${media.mobile} {
    margin-bottom: 4vw;
  }
`

const Text = styled.p`
  ${text.bodyR};
  ${media.fullWidth} {
    margin-bottom: 30px;
  }
  ${media.desktop} {
    margin-bottom: 1.807vw;
  }
  ${media.tablet} {
    margin-bottom: 2.93vw;
  }
  ${media.mobile} {
    ${text.bodyS};
    margin-bottom: 8vw;
  }
`
